'use strict'

const yaml = require('js-yaml')
const { scanCritic, SUGGESTION_TYPES } = require('./critic-markup')

// Statuses from which a spec can be approved; mirrors the spec-board columns
const APPROVABLE = ['ready-for-review', 'in-review']
// Ordered phases; the author advances one step at a time up to in-review
// (approval flips to approved, an implements-commit flips to implemented).
const PHASES = ['draft', 'ready-for-review', 'in-review', 'approved', 'implemented']
const NEXT_PHASE = { draft: 'ready-for-review', 'ready-for-review': 'in-review' }

function lc (s) {
  return String(s).trim().toLowerCase()
}

function normList (value) {
  if (value == null) return []
  const raw = Array.isArray(value) ? value : String(value).split(',')
  return raw.map(v => String(v).trim()).filter(Boolean)
}

function frontmatter (text) {
  if (!text.startsWith('---')) return null
  const end = text.indexOf('\n---', 3)
  if (end === -1) return null
  try {
    return { meta: yaml.load(text.slice(3, end)) || {}, end }
  } catch (e) {
    return null
  }
}

// Who may approve comes only from the namespace repo's .specs/roles.yml, which
// is protected by branch protection. The editable note never grants approval
// rights; it only holds the approved-by record.
function effectiveRule (roles) {
  const approvers = normList(roles && roles.approvers)
  if (!approvers.length) return null
  // Same semantics as the board: explicit 0 turns quorum off, malformed
  // values default to 1.
  const reqRaw = Number(roles['approvals-required'])
  const required = Math.min(approvers.length, Number.isInteger(reqRaw) && reqRaw >= 0 ? reqRaw : 1)
  return { approvers, required, source: 'namespace' }
}

// Markup the board refuses to publish around: an open thread, or a suggestion
// nobody accepted or rejected (the PR is written with suggestions in their
// accepted form). Recording an approval is still allowed with either pending,
// since the board holds the PR rather than the approval.
function pendingReview (text) {
  const spans = scanCritic(text)
  return {
    openThreads: spans.filter(s => s.type === 'comment' && !s.resolved).length,
    pendingSuggestions: spans.filter(s => SUGGESTION_TYPES.includes(s.type)).length
  }
}

// Items of a flow list with their offsets into the inner text, so one name
// can be removed or replaced without retyping the others: every character
// carries its author, and retyping a name makes it this session's.
function flowItems (inner) {
  const items = []
  const re = /[^,]+/g
  let m
  while ((m = re.exec(inner)) !== null) {
    const lead = m[0].length - m[0].trimStart().length
    const name = m[0].trim()
    if (name) items.push({ name, start: m.index + lead, end: m.index + lead + name.length })
  }
  return items
}

// Edit deleting one item and its separator from a flow list at listStart.
function dropItemEdit (inner, listStart, i) {
  const items = flowItems(inner)
  const from = i > 0 ? items[i - 1].end : items[i].start
  const to = i > 0 ? items[i].end : (items[i + 1] ? items[i + 1].start : items[i].end)
  return { from: listStart + from, to: listStart + to, text: '' }
}

/**
 * Approval state of a spec note for a given user.
 * @param {string} text full note markdown
 * @param {string} userName current user's GitHub login or display name
 * @param {object} [roles] namespace roles ({approvers, 'approvals-required'}) when the note lists none
 * @param {object} [record] the board's record for the note; its approvedBy is the
 *   attested list, the names the board saw written by their owners, and only
 *   those count. null or absent: the board has not answered, the list stands.
 * @param {boolean|null} [self] this session's own action, which outranks a
 *   record older than it: true after approving here, false after retracting
 */
function specApprovalState (text, userName, roles, record, self = null) {
  const state = {
    isSpec: false,
    namespace: '',
    approvers: [],
    approvedBy: [],
    credited: [],
    unattested: [],
    stale: [],
    changesUrl: '',
    attested: false,
    required: 0,
    source: '',
    status: '',
    approvable: false,
    nextPhase: '',
    approvals: 0,
    isApprover: false,
    canApprove: false,
    hasApproved: false,
    openThreads: 0,
    pendingSuggestions: 0
  }
  const fm = frontmatter(text)
  if (!fm) return state
  const tags = normList(fm.meta.tags).map(lc)
  if (!tags.includes('spec')) return state
  state.isSpec = true
  Object.assign(state, pendingReview(text))
  state.namespace = fm.meta.namespace ? String(fm.meta.namespace).trim() : ''
  state.approvedBy = normList(fm.meta['approved-by'])
  state.status = PHASES.find(s => tags.includes(s)) || ''
  state.approvable = APPROVABLE.includes(state.status)
  state.nextPhase = NEXT_PHASE[state.status] || ''
  const listed = new Set(state.approvedBy.map(lc))
  const attestedList = record && Array.isArray(record.approvedBy) ? record.approvedBy.map(lc) : null
  state.attested = !!attestedList
  const approved = new Set(attestedList ? attestedList.filter(a => listed.has(a)) : listed)
  const me = lc(userName || '')
  if (me && listed.has(me) && self === true) approved.add(me)
  if (me && self === false) approved.delete(me)
  state.credited = state.approvedBy.filter(a => approved.has(lc(a)))
  state.unattested = state.approvedBy.filter(a => !approved.has(lc(a)))
  // Approvals the board reports the text has moved past; still credited.
  const staleList = record && Array.isArray(record.stale) ? record.stale.map(lc) : []
  state.stale = state.credited.filter(a => staleList.includes(lc(a)))
  state.changesUrl = record && typeof record.changesUrl === 'string' ? record.changesUrl : ''
  state.hasApproved = !!me && approved.has(me)
  const rule = effectiveRule(roles)
  if (!rule) return state
  state.approvers = rule.approvers
  state.required = rule.required
  state.source = rule.source
  state.approvals = rule.approvers.filter(a => approved.has(lc(a))).length
  state.isApprover = !!me && rule.approvers.some(a => lc(a) === me)
  state.canApprove = state.isApprover && !state.hasApproved && state.approvable
  return state
}

// Frontmatter slice (up to the closing delimiter); '' when there is none.
function fmSlice (text) {
  const fm = frontmatter(text)
  return fm && fm.end !== -1 ? text.slice(0, fm.end) : ''
}

// Edit that replaces the current phase tag in the tags line with newStatus,
// or null when the tags line has no recognized phase. Offsets are into text.
function phaseTagEdit (fm, newStatus) {
  const tagsLine = /^tags:.*$/m.exec(fm)
  if (!tagsLine) return null
  const m = new RegExp('\\b(' + PHASES.join('|') + ')\\b').exec(tagsLine[0])
  if (!m) return null
  const from = tagsLine.index + m.index
  return { from, to: from + m[0].length, text: newStatus }
}

// Apply computed edits (descending by position) through the editor's change
// pipeline so realtime collaboration stays consistent; buffer re-read by caller.
function applyEdits (editor, edits, origin) {
  if (!edits) return false
  for (const e of edits) {
    editor.replaceRange(e.text, editor.posFromIndex(e.from), editor.posFromIndex(e.to), origin)
  }
  return true
}

/**
 * Edits recording userName's approval: append to the approved-by flow list
 * (creating it after the approvers: or tags: line when absent), and flip the
 * phase tag to approved once this approval meets quorum. Returns null when the
 * user cannot approve or the frontmatter is not editable flow-style.
 */
function computeApprovalEdits (text, userName, roles, record, self) {
  const state = specApprovalState(text, userName, roles, record, self)
  if (!state.canApprove) return null
  const name = String(userName).trim()
  const fm = fmSlice(text)
  const edits = []

  const abMatch = /^approved-by:[ \t]*\[([^\]\n]*)\]/m.exec(fm)
  if (abMatch) {
    const insertAt = abMatch.index + abMatch[0].length - 1
    const listStart = insertAt - abMatch[1].length
    const items = flowItems(abMatch[1])
    // A name already there that the board does not attest was typed by
    // someone else: it goes, and the insertion below is this session's own.
    const mine = items.findIndex(it => lc(it.name) === lc(name))
    if (mine !== -1) edits.push(dropItemEdit(abMatch[1], listStart, mine))
    // The board attests the delimiter with the name, so the separator is
    // typed here too: the comma before a further name, or the opening
    // bracket retyped when the name will be the only one.
    if (items.length > (mine === -1 ? 0 : 1)) edits.push({ from: insertAt, to: insertAt, text: ', ' + name })
    else edits.push({ from: listStart - 1, to: listStart, text: '[' + name })
  } else if (/^approved-by:/m.test(fm)) {
    // An empty value (null to YAML) takes the list in place; a block list or
    // scalar is left alone, since a second approved-by: key fails the parse.
    const bare = /^approved-by:([ \t]*)$(?!\n[ \t]+-)/m.exec(fm)
    if (!bare) return null
    const from = bare.index + 'approved-by:'.length
    edits.push({ from, to: from + bare[1].length, text: ' [' + name + ']' })
  } else {
    const anchor = /^approvers:[ \t]*\[[^\]\n]*\][ \t]*$/m.exec(fm) || /^tags:.*$/m.exec(fm)
    if (!anchor) return null
    const lineEnd = anchor.index + anchor[0].length
    edits.push({ from: lineEnd, to: lineEnd, text: '\napproved-by: [' + name + ']' })
  }

  const after = new Set(state.approvedBy.concat(name).map(lc))
  if (state.approvers.filter(a => after.has(lc(a))).length >= state.required) {
    const flip = phaseTagEdit(fm, 'approved')
    if (flip) edits.push(flip)
  }
  return edits.sort((a, b) => b.from - a.from)
}

function applyApproval (editor, userName, roles, record, self) {
  return applyEdits(editor, computeApprovalEdits(editor.getValue(), userName, roles, record, self), '+specApprove')
}

/**
 * Edits removing userName from approved-by, reverting an auto-flipped approved
 * phase back to in-review when the retraction drops below quorum. Returns null
 * if the user hasn't approved or the list isn't flow style.
 */
function computeUnapprovalEdits (text, userName, roles, record, self) {
  const state = specApprovalState(text, userName, roles, record, self)
  if (!state.hasApproved) return null
  const fm = fmSlice(text)
  const m = /^(approved-by:[ \t]*\[)([^\]\n]*)(\])/m.exec(fm)
  if (!m) return null
  const me = lc(userName)
  const items = flowItems(m[2])
  const mine = items.findIndex(it => lc(it.name) === me)
  if (mine === -1) return null
  // Only this name leaves; retyping the others would make them this session's.
  const edits = [dropItemEdit(m[2], m.index + m[1].length, mine)]
  const approved = new Set(state.credited.map(lc))
  approved.delete(me)
  const metAfter = state.approvers.filter(a => approved.has(lc(a))).length >= state.required
  if (!metAfter && state.status === 'approved') {
    const revert = phaseTagEdit(fm, 'in-review')
    if (revert) edits.push(revert)
  }
  return edits.sort((a, b) => b.from - a.from)
}

function applyUnapproval (editor, userName, roles, record, self) {
  return applyEdits(editor, computeUnapprovalEdits(editor.getValue(), userName, roles, record, self), '+specApprove')
}

/**
 * Edits replacing the spec's current phase tag with newStatus (a PHASES value).
 * Returns null when the note has no tags line or no recognized phase tag.
 */
function computeStatusEdits (text, newStatus) {
  const edit = phaseTagEdit(fmSlice(text), newStatus)
  return edit ? [edit] : null
}

function applyStatus (editor, newStatus) {
  return applyEdits(editor, computeStatusEdits(editor.getValue(), newStatus), '+specPhase')
}

module.exports = { specApprovalState, pendingReview, computeApprovalEdits, applyApproval, computeUnapprovalEdits, applyUnapproval, applyEdits, computeStatusEdits, applyStatus, PHASES, NEXT_PHASE }
